import React, { useEffect, useState } from 'react'

import { groupBy, isEmpty } from 'lodash'
import { Controller, FormContext, useForm } from 'react-hook-form'
import clsx from 'clsx'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Tab,
  Tabs,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
} from '@material-ui/core'
import {
  AlertTriangle as AlertTriangleIcon,
  Award as AwardIcon,
  Edit2 as Edit2Icon,
} from 'react-feather'

import { LoadingFeedback, PrivacyByDesignEvidenceField } from 'components'

import constants from 'constants/index'
import schema from './schema'
import useStyles from './styles'

const { JUSTIFY_MAX_LENGTH, SIMPLE_ANSWERS } = constants.privacyByDesign
const { NON_CONFORMITY_TYPE, IMPROVEMENT_TYPE } =
  constants.privacyByDesignAuditEntity

const EMPTY_EVIDENCES = []

// Uma questão = um formulário próprio (react-hook-form + schema) com o próprio
// botão Salvar, no mesmo recorte do endpoint de resposta. Já respondida, ela é
// somente leitura até ser reaberta.
const QuestionReplyItem = ({
  question,
  index,
  isShow,
  evidencesByAnswerId,
  onRemoveEvidence,
  onSaveAnswer,
  onReopenAnswer,
  saveState,
}) => {
  const classes = useStyles()

  const { ANSWERED_STATUS, PENDING_STATUS, STATUSES } =
    constants.privacyByDesignAnswer

  const answer = question.privacyByDesignAnswers?.[0]
  // EMPTY_EVIDENCES evita criar um array novo a cada render, o que faria o
  // PrivacyByDesignEvidenceField ressemear o campo indefinidamente.
  const answerEvidences =
    evidencesByAnswerId[answer?.id] ?? answer?.evidences ?? EMPTY_EVIDENCES

  // Apontamentos que o revisor registrou nesta questão.
  const auditEntitiesByType = groupBy(
    question.auditEntities || [],
    'auditEntityType',
  )
  const nonConformities = auditEntitiesByType[NON_CONFORMITY_TYPE] || []
  const improvements = auditEntitiesByType[IMPROVEMENT_TYPE] || []

  const answerValues = {
    answer: answer?.answer || '',
    justify: answer?.justify || '',
    questionAlternativeId: answer?.questionAlternativeId || '',
    // A evidência faz parte do formulário: os arquivos ficam aqui até o save.
    evidences: isEmpty(answerEvidences) ? null : answerEvidences,
  }

  // Cada questão tem o próprio formulário: valida e salva de forma independente,
  // no mesmo recorte do endpoint de resposta.
  const formMethods = useForm({
    validationSchema: schema(question),
    defaultValues: answerValues,
  })

  const { control, watch, errors, reset, handleSubmit, clearError } =
    formMethods

  const selectedAlternativeId = watch('questionAlternativeId')
  const answerValue = watch('answer')

  const selectedAlternative = question.questionAlternatives?.find(
    (alternative) => alternative.id === Number(selectedAlternativeId),
  )
  const evidenceRequired = !!selectedAlternative?.evidencesRequired
  // A justificativa aparece sempre; a obrigatoriedade vem da alternativa
  // selecionada (questões Sim/Parcial/Não não têm a flag, logo é opcional).
  const justifyRequired = !!selectedAlternative?.observationsRequired

  const isSaving = saveState === 'saving'
  // Questão respondida é somente leitura: para alterá-la é preciso reabrir.
  const isAnswered = answer?.status === ANSWERED_STATUS
  const isReadOnly = isShow || isAnswered

  // A resposta salva é a referência do formulário; ao voltar do backend
  // (inclusive após anexar evidência) o form é ressincronizado.
  useEffect(() => {
    reset(answerValues)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answer?.answer, answer?.justify, answer?.questionAlternativeId])

  // O handleSubmit valida o schema da questão e só chama o save quando passa,
  // marcando os erros dos campos que faltam. Do campo de evidência vão apenas os
  // arquivos novos; os já salvos ficam no backend.
  const handleSave = handleSubmit(async (values) => {
    if (isReadOnly || isSaving) return

    const newEvidences = values.evidences
      ? Array.from(values.evidences).filter((file) => file instanceof File)
      : []

    // Nas questões com alternativas o texto da resposta é o título da
    // alternativa marcada — o campo `answer` do formulário só existe nas
    // questões de escala (Sim/Parcial/Não).
    const answerText = selectedAlternative
      ? selectedAlternative.title || selectedAlternative.description || ''
      : values.answer

    await onSaveAnswer?.(question.id, answer?.id, {
      ...values,
      answer: answerText,
      evidences: newEvidences,
    })
  })

  return (
    <FormContext {...formMethods}>
      {/* Fora dos accordions por status, a cor da faixa do cabeçalho e o chip
          são o que dizem se a questão está pendente ou respondida. */}
      <Box className={classes.questionCard} id={question.id}>
        {/* A pergunta é o conteúdo principal do card, então fica em destaque; o
            número vira um selo e o tema, que só classifica a pergunta, uma
            linha de apoio acima dela. */}
        <Box
          className={clsx(classes.questionHeader, {
            [classes.questionHeaderAnswered]: isAnswered,
            [classes.questionHeaderPending]: !isAnswered,
          })}
        >
          <Box className={classes.questionHeading}>
            <Box className={classes.titleRow}>
              <Typography variant="subtitle2" className={classes.themeLabel}>
                {index + 1}. {question.theme}
              </Typography>
              <Chip
                size="small"
                className={clsx(classes.statusChip, {
                  [classes.chipAnswered]: isAnswered,
                  [classes.chipPending]: !isAnswered,
                })}
                label={STATUSES[isAnswered ? ANSWERED_STATUS : PENDING_STATUS]}
              />
              {saveState === 'saving' && (
                <Typography
                  variant="body2"
                  className={clsx(classes.saveStateBadge, classes.savingText)}
                >
                  Salvando…
                </Typography>
              )}
              {saveState === 'saved' && (
                <Typography
                  variant="body2"
                  className={clsx(classes.saveStateBadge, classes.savedText)}
                >
                  Salvo
                </Typography>
              )}
            </Box>
            <Typography variant="h5" className={classes.questionText}>
              {question.question}
            </Typography>
          </Box>
        </Box>
        <Box className={classes.questionBody}>
          <Box>
            {/* Sim/Parcial/Não também são alternativas, então o label é o mesmo
                das questões com alternativas cadastradas. */}
            <Typography variant="subtitle2" gutterBottom>
              Alternativas
            </Typography>
            {!isEmpty(question.questionAlternatives) ? (
              <Controller
                control={control}
                name="questionAlternativeId"
                defaultValue={answerValues.questionAlternativeId}
                as={
                  <RadioGroup className={classes.optionsGroup}>
                    {question.questionAlternatives.map((alternative) => (
                      <FormControlLabel
                        key={alternative.id}
                        value={alternative.id}
                        control={<Radio color="primary" />}
                        disabled={isReadOnly}
                        className={classes.option}
                        label={
                          <Typography
                            variant="body1"
                            className={clsx({
                              [classes.optionSelectedLabel]:
                                Number(selectedAlternativeId) ===
                                alternative.id,
                            })}
                          >
                            {alternative.title}
                          </Typography>
                        }
                      />
                    ))}
                  </RadioGroup>
                }
                onChange={([event]) => {
                  // Trocar a alternativa muda as obrigatoriedades: os erros
                  // da seleção anterior saem da tela.
                  clearError()
                  return event.target.value ? Number(event.target.value) : ''
                }}
              />
            ) : (
              <Controller
                control={control}
                name="answer"
                defaultValue={answerValues.answer}
                as={
                  <RadioGroup className={classes.optionsGroup}>
                    {SIMPLE_ANSWERS.map((simpleAnswer) => (
                      <FormControlLabel
                        key={simpleAnswer}
                        value={simpleAnswer}
                        control={<Radio color="primary" />}
                        disabled={isReadOnly}
                        className={classes.option}
                        label={
                          <Typography
                            variant="body1"
                            className={clsx({
                              [classes.optionSelectedLabel]:
                                answerValue === simpleAnswer,
                            })}
                          >
                            {simpleAnswer}
                          </Typography>
                        }
                      />
                    ))}
                  </RadioGroup>
                }
                onChange={([event]) => event.target.value}
              />
            )}
          </Box>
          <Controller
            control={control}
            name="justify"
            defaultValue={answerValues.justify}
            as={
              <TextField
                fullWidth
                multiline
                minRows={3}
                maxRows={5}
                label="Justificativa"
                variant="outlined"
                inputProps={{ maxLength: JUSTIFY_MAX_LENGTH }}
                required={justifyRequired}
                error={!!errors?.justify}
                helperText={errors?.justify?.message}
                disabled={isReadOnly}
              />
            }
          />
          {/* O campo aparece junto com a alternativa que exige evidência, mesmo
              antes de existir resposta salva: é ela que o schema valida e o
              arquivo vai no mesmo request do primeiro save. */}
          {evidenceRequired && (
            <PrivacyByDesignEvidenceField
              answerId={answer?.id}
              required
              readOnly={isReadOnly}
              evidences={answerEvidences}
              loading={isSaving}
              controlName="evidences"
              onRemove={onRemoveEvidence}
            />
          )}
          {/* Apontamentos do revisor na própria questão: são somente leitura, o
              que a organização faz com eles é corrigir a resposta. */}
          {!isEmpty(nonConformities) && (
            <Box>
              <Typography
                variant="subtitle2"
                gutterBottom
                className={classes.auditLabel}
              >
                <AlertTriangleIcon size={14} />
                Não Conformidades
              </Typography>
              <Box
                className={clsx(
                  classes.auditBox,
                  classes.auditBoxNonConformity,
                )}
              >
                {nonConformities.map((auditEntity) => (
                  <Typography key={auditEntity.id} variant="body2">
                    {auditEntity.content}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
          {!isEmpty(improvements) && (
            <Box>
              <Typography
                variant="subtitle2"
                gutterBottom
                className={classes.auditLabel}
              >
                <AwardIcon size={14} />
                Melhorias
              </Typography>
              <Box
                className={clsx(classes.auditBox, classes.auditBoxImprovement)}
              >
                {improvements.map((auditEntity) => (
                  <Typography key={auditEntity.id} variant="body2">
                    {auditEntity.content}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
        </Box>
        {(!isShow || !!answer?.replierName) && (
          <Box className={classes.questionFooter}>
            {(!!answer?.answer || !!answer?.questionAlternativeId) &&
              !!answer?.replierName && (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  className={classes.replierName}
                >
                  Respondido por:{' '}
                  <span className={classes.replierNameHighlight}>
                    {answer?.replierName}
                  </span>
                </Typography>
              )}
            {isShow ? null : isAnswered ? (
              <Button
                color="primary"
                variant="outlined"
                onClick={() => onReopenAnswer?.(question.id, answer?.id)}
                startIcon={<Edit2Icon size={14} />}
              >
                Reabrir
              </Button>
            ) : (
              <Button
                color="primary"
                variant="contained"
                disabled={isSaving}
                onClick={handleSave}
                startIcon={
                  isSaving && <CircularProgress size={14} color="inherit" />
                }
              >
                Salvar
              </Button>
            )}
          </Box>
        )}
      </Box>
    </FormContext>
  )
}

const PrivacyByDesignQuestionReply = ({
  questions,
  isLoading,
  isShow = false,
  evidencesByAnswerId = {},
  onRemoveEvidence,
  onSaveAnswer,
  onReopenAnswer,
  saveStateByQuestionId = {},
  hasReviewerOpinion = false,
  // Abas e lista de questões são passos distintos do tour, então cada uma tem o
  // próprio âncora. Os ids mudam por audiência (app x portal).
  id = constants.privacyByDesign.PRIVACY_BY_DESIGN_FORM_DRIVER_STEP_2,
  tabsId = constants.privacyByDesign.PRIVACY_BY_DESIGN_FORM_DRIVER_STEP_TABS,
}) => {
  const classes = useStyles()

  const [currentTab, setCurrentTab] = useState(constants.questions.ALL_TAB)

  const { ANSWERED_STATUS, PENDING_STATUS } = constants.privacyByDesignAnswer
  const { ALL_TAB, PENDING_TAB, ANSWERED_TAB, TABS } = constants.questions

  const allQuestions = questions || []

  const pendingQuestions = allQuestions.filter(
    (question) =>
      !question.privacyByDesignAnswers?.[0] ||
      question.privacyByDesignAnswers[0].status === PENDING_STATUS,
  )

  const answeredQuestions = allQuestions.filter(
    (question) =>
      question.privacyByDesignAnswers?.[0]?.status === ANSWERED_STATUS,
  )

  // Em "Todas" as pendentes vêm primeiro: são as que ainda precisam de ação.
  const questionsByTab = {
    [ALL_TAB]: [...pendingQuestions, ...answeredQuestions],
    [PENDING_TAB]: pendingQuestions,
    [ANSWERED_TAB]: answeredQuestions,
  }

  const displayedQuestions = questionsByTab[currentTab]

  return (
    <>
      <LoadingFeedback open={isLoading} />
      <Box>
        {/* Uma aba por status: a lista fica plana e a questão pendente não
            exige abrir nada para aparecer. A contagem vai no label das abas. */}
        <Tabs
          id={tabsId}
          onChange={(_, value) => setCurrentTab(value)}
          scrollButtons="auto"
          value={currentTab}
          variant="scrollable"
          textColor="primary"
          indicatorColor="primary"
        >
          {TABS.map((tab) => (
            <Tab
              key={tab.value}
              value={tab.value}
              label={`${tab.label} (${questionsByTab[tab.value].length})`}
            />
          ))}
        </Tabs>
        <Box id={id} className={classes.questionsList}>
          {isEmpty(displayedQuestions) ? (
            <Typography variant="body1">
              Não existe nenhuma questão com este status.
            </Typography>
          ) : (
            displayedQuestions.map((question) => (
              <QuestionReplyItem
                key={question.id}
                question={question}
                // A numeração é a posição da questão no questionário, não na
                // lista exibida: ela não muda ao filtrar nem ao reordenar.
                index={allQuestions.indexOf(question)}
                isShow={isShow}
                evidencesByAnswerId={evidencesByAnswerId}
                onRemoveEvidence={onRemoveEvidence}
                onSaveAnswer={onSaveAnswer}
                onReopenAnswer={onReopenAnswer}
                saveState={saveStateByQuestionId[question.id]}
              />
            ))
          )}
        </Box>
      </Box>
    </>
  )
}

export default PrivacyByDesignQuestionReply
